import { useEffect, useRef, useState } from 'react';
import type {
  FredrickOwlPageCandidateV1,
  FredrickSourceImageId,
} from 'fredrick-data-contract';
import type {
  FredrickImportSessionDraftV1,
  FredrickImportSessionViewV1,
} from 'fredrick-local-repository';
import { StatusPill } from '../components/StatusPill';
import { downloadFredrickDebugExport } from '../lib/debugExport';
import { dateTime, formatMeso } from '../lib/format';
import { completeOwlImport, prepareSourceImage } from '../lib/ingest';
import { continuePersistedImportForMountedView } from '../lib/importPersistence';
import { scheduleLocalMarketProcessing } from '../lib/marketProcessing';
import { localRepository } from '../lib/repository';
import { deriveObservedAt } from '../lib/time';

type SessionProposal = FredrickImportSessionViewV1['proposals'][number];
type CardPersistence = 'saved' | 'reused' | 'duplicate' | null;
type NoticeTone = 'good' | 'warn' | 'bad';

interface PageNotice {
  readonly tone: NoticeTone;
  readonly text: string;
}

interface ImportCard {
  readonly id: string;
  readonly sessionEntryIndex: number | null;
  readonly fileName: string;
  readonly previewUrl: string;
  readonly observedAt: number;
  readonly sourceImageId: FredrickSourceImageId | null;
  readonly candidate: FredrickOwlPageCandidateV1 | null;
  readonly persistence: CardPersistence;
  readonly duplicateOfIndex: number | null;
  readonly error: string | null;
  readonly busy: boolean;
}

function cardId(): string {
  return `import-${crypto.randomUUID()}`;
}

function isPng(file: File): boolean {
  return file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
}

function proposalLabel(status: SessionProposal['status']): string {
  switch (status) {
    case 'new-search': return 'New search';
    case 'extended-existing-search': return 'Continue search';
    case 'already-covered': return 'Already covered';
    case 'conflicting-continuation': return 'Page conflict';
    case 'ambiguous-lineage': return 'Ambiguous lineage';
    case 'unassigned-page': return 'Unassigned';
  }
}

function proposalTone(status: SessionProposal['status']): 'good' | 'warn' | 'neutral' {
  switch (status) {
    case 'new-search':
    case 'extended-existing-search':
      return 'good';
    case 'already-covered':
      return 'neutral';
    case 'conflicting-continuation':
    case 'ambiguous-lineage':
    case 'unassigned-page':
      return 'warn';
  }
}

function proposalExplanation(proposal: SessionProposal): string {
  switch (proposal.status) {
    case 'new-search':
      return 'No page in this set belongs to an existing local lineage. Finishing creates a new search root.';
    case 'extended-existing-search':
      return 'A repeated Owl page anchors this set to one existing lineage. Finishing appends a strict-superset continuation.';
    case 'already-covered':
      return 'Every distinct Owl page in this set is already represented by the accepted lineage head. No new claim is needed.';
    case 'conflicting-continuation':
      return 'This set would replace an occupied page with different Owl-window evidence. It will not create a grouping claim.';
    case 'ambiguous-lineage':
      return 'The sources point to more than one lineage, or the matched lineage has no single accepted head. It will not be merged automatically.';
    case 'unassigned-page':
      return 'The current preferred revision is unavailable or not market-eligible. The capture remains stored but will not enter a search claim.';
  }
}

function sessionEntryPersistence(
  entry: FredrickImportSessionViewV1['entries'][number],
  sessionStartedAt: number,
): Exclude<CardPersistence, null> {
  if (entry.duplicateOfIndex !== null) return 'duplicate';
  return entry.capture.importedAt < sessionStartedAt ? 'reused' : 'saved';
}

export function ImportPage() {
  const [cards, setCardsState] = useState<ImportCard[]>([]);
  const [session, setSessionState] = useState<FredrickImportSessionDraftV1 | null>(null);
  const [proposals, setProposals] = useState<readonly SessionProposal[]>([]);
  const [dragging, setDragging] = useState(false);
  const [sessionBusy, setSessionBusy] = useState(true);
  const [exportBusy, setExportBusy] = useState(false);
  const [message, setMessage] = useState<PageNotice | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const cardsRef = useRef<ImportCard[]>([]);
  const sessionRef = useRef<FredrickImportSessionDraftV1 | null>(null);
  const previewUrlsRef = useRef(new Set<string>());

  function setCards(next: ImportCard[] | ((current: ImportCard[]) => ImportCard[])): void {
    setCardsState((current) => {
      const value = typeof next === 'function' ? next(current) : next;
      cardsRef.current = value;
      return value;
    });
  }

  function setSession(next: FredrickImportSessionDraftV1 | null): void {
    sessionRef.current = next;
    setSessionState(next);
  }

  function createPreviewUrl(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    previewUrlsRef.current.add(url);
    return url;
  }

  function revokePreviewUrl(url: string): void {
    if (!previewUrlsRef.current.delete(url)) return;
    URL.revokeObjectURL(url);
  }

  function revokeAllCardPreviews(): void {
    for (const card of cardsRef.current) revokePreviewUrl(card.previewUrl);
  }

  function replaceCardPreview(id: string, previousUrl: string, blob: Blob): string {
    const previewUrl = createPreviewUrl(blob);
    setCards((current) => current.map((value) => value.id === id
      ? { ...value, previewUrl }
      : value));
    window.setTimeout(() => revokePreviewUrl(previousUrl), 0);
    return previewUrl;
  }

  function applySessionPlan(view: FredrickImportSessionViewV1): void {
    setSession(view.session);
    setProposals(view.proposals);
  }

  function reconcileSessionCards(view: FredrickImportSessionViewV1): void {
    const entryByIndex = new Map(view.entries.map((entry) => [entry.index, entry]));
    setCards((current) => current.map((card) => {
      if (card.sessionEntryIndex === null) return card;
      const entry = entryByIndex.get(card.sessionEntryIndex);
      if (entry === undefined) return card;
      return {
        ...card,
        duplicateOfIndex: entry.duplicateOfIndex,
        persistence: sessionEntryPersistence(entry, view.session.startedAt),
      };
    }));
  }

  function hydrateSession(view: FredrickImportSessionViewV1): void {
    revokeAllCardPreviews();
    const hydrated = view.entries.map((entry): ImportCard => ({
      id: cardId(),
      sessionEntryIndex: entry.index,
      fileName: entry.capture.fileName ?? `Owl screenshot ${entry.index + 1}`,
      previewUrl: createPreviewUrl(entry.evidence.blob),
      observedAt: entry.capture.observedAt,
      sourceImageId: entry.sourceImageId,
      candidate: entry.candidate.candidate,
      persistence: sessionEntryPersistence(entry, view.session.startedAt),
      duplicateOfIndex: entry.duplicateOfIndex,
      error: null,
      busy: false,
    }));
    setCards(hydrated);
    applySessionPlan(view);
  }

  async function restoreSession(): Promise<void> {
    setSessionBusy(true);
    try {
      const view = await localRepository.getOrCreateOpenImportSession();
      if (!mountedRef.current) return;
      hydrateSession(view);
    } catch (error) {
      if (!mountedRef.current) return;
      setMessage({
        tone: 'bad',
        text: error instanceof Error ? error.message : 'Could not open the import session.',
      });
    } finally {
      if (mountedRef.current) setSessionBusy(false);
    }
  }

  async function ensureSession(): Promise<FredrickImportSessionDraftV1> {
    if (sessionRef.current !== null) return sessionRef.current;
    const view = await localRepository.getOrCreateOpenImportSession();
    if (mountedRef.current) applySessionPlan(view);
    return view.session;
  }

  async function refreshSessionPlan(): Promise<void> {
    const view = await localRepository.getOpenImportSession();
    if (!mountedRef.current) return;
    if (view === null) {
      setSession(null);
      setProposals([]);
      return;
    }
    applySessionPlan(view);
    reconcileSessionCards(view);
  }

  useEffect(() => {
    mountedRef.current = true;
    void restoreSession();

    const paste = (event: ClipboardEvent) => {
      const files = [...(event.clipboardData?.files ?? [])].filter(isPng);
      if (!files.length) return;
      event.preventDefault();
      void addFiles(files, Date.now());
    };
    window.addEventListener('paste', paste);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('paste', paste);
      for (const url of previewUrlsRef.current) URL.revokeObjectURL(url);
      previewUrlsRef.current.clear();
    };
  }, []);

  async function loadStoredCard(
    id: string,
    sourceImageId: FredrickSourceImageId,
    sessionEntryIndex: number,
    duplicateOfIndex: number | null,
    previousUrl: string,
  ): Promise<void> {
    const persistence: Exclude<CardPersistence, 'saved' | null> = duplicateOfIndex === null
      ? 'reused'
      : 'duplicate';
    setCards((current) => current.map((value) => value.id === id
      ? { ...value, sourceImageId, sessionEntryIndex, duplicateOfIndex, persistence }
      : value));

    const existing = await localRepository.getCaptureBundle(sourceImageId);
    if (existing === null) throw new Error('The retained capture disappeared from the local repository.');
    const existingEvidence = await localRepository.getEvidence(
      existing.candidate.candidate.evidence.evidenceImageId,
    );
    if (existingEvidence === null) {
      throw new Error('The retained capture is missing its private Owl evidence.');
    }
    if (!mountedRef.current) return;

    const previewUrl = replaceCardPreview(id, previousUrl, existingEvidence.blob);
    setCards((current) => current.map((value) => value.id === id
      ? {
          ...value,
          previewUrl,
          sourceImageId,
          sessionEntryIndex,
          duplicateOfIndex,
          observedAt: existing.capture.observedAt,
          candidate: existing.candidate.candidate,
          persistence,
          error: null,
          busy: false,
        }
      : value));
  }

  async function addFiles(files: readonly File[], fallbackAt = Date.now()): Promise<void> {
    const accepted = files.filter(isPng);
    if (!accepted.length) {
      setMessage({ tone: 'bad', text: 'Only opaque PNG screenshots are supported.' });
      return;
    }

    let activeSession: FredrickImportSessionDraftV1;
    try {
      activeSession = await ensureSession();
    } catch (error) {
      setMessage({
        tone: 'bad',
        text: error instanceof Error ? error.message : 'Could not open the import session.',
      });
      return;
    }

    const importedAt = Date.now();
    const additions = accepted.map((file): ImportCard & { readonly file: File } => ({
      id: cardId(),
      file,
      sessionEntryIndex: null,
      fileName: file.name || 'Pasted screenshot',
      previewUrl: createPreviewUrl(file),
      observedAt: deriveObservedAt(file, fallbackAt).observedAt,
      sourceImageId: null,
      candidate: null,
      persistence: null,
      duplicateOfIndex: null,
      error: null,
      busy: true,
    }));

    setCards((current) => [...current, ...additions]);
    setMessage(null);

    for (const addition of additions) {
      let previewUrl = addition.previewUrl;
      try {
        const preparedSource = await prepareSourceImage(addition.file);
        if (!mountedRef.current) return;

        if (await localRepository.hasSourceImage(preparedSource.sourceImage.sourceImageId)) {
          const appended = await localRepository.appendImportSessionEntry(
            activeSession.sessionId,
            preparedSource.sourceImage.sourceImageId,
          );
          if (!mountedRef.current) return;
          await loadStoredCard(
            addition.id,
            preparedSource.sourceImage.sourceImageId,
            appended.sessionEntryIndex,
            appended.duplicateOfIndex,
            previewUrl,
          );
          continue;
        }

        const prepared = await completeOwlImport(preparedSource, {
          observedAt: addition.observedAt,
          importedAt,
        });
        if (!mountedRef.current) return;
        previewUrl = replaceCardPreview(addition.id, previewUrl, prepared.evidenceBlob);

        const result = await localRepository.importPreparedPage({
          sessionId: activeSession.sessionId,
          candidate: prepared.candidate,
          evidenceBlob: prepared.evidenceBlob,
          ...(addition.file.name ? { fileName: addition.file.name } : {}),
        });
        const visibleResult = continuePersistedImportForMountedView(
          result,
          scheduleLocalMarketProcessing,
          () => mountedRef.current,
        );
        if (visibleResult === null) return;

        if (visibleResult.disposition === 'duplicate') {
          await loadStoredCard(
            addition.id,
            visibleResult.sourceImageId,
            visibleResult.sessionEntryIndex,
            visibleResult.duplicateOfIndex,
            previewUrl,
          );
          continue;
        }

        setCards((current) => current.map((value) => value.id === addition.id
          ? {
              ...value,
              previewUrl,
              sessionEntryIndex: visibleResult.sessionEntryIndex,
              sourceImageId: visibleResult.sourceImageId,
              candidate: prepared.candidate,
              persistence: visibleResult.duplicateOfIndex === null ? 'saved' : 'duplicate',
              duplicateOfIndex: visibleResult.duplicateOfIndex,
              error: null,
              busy: false,
            }
          : value));
      } catch (error) {
        if (!mountedRef.current) return;
        setCards((current) => current.map((value) => value.id === addition.id
          ? {
              ...value,
              error: error instanceof Error ? error.message : 'Could not save screenshot.',
              busy: false,
            }
          : value));
      }
    }

    await refreshSessionPlan();
  }

  async function removeCard(card: ImportCard): Promise<void> {
    if (card.busy) return;
    if (card.sessionEntryIndex === null) {
      revokePreviewUrl(card.previewUrl);
      setCards((current) => current.filter((value) => value.id !== card.id));
      return;
    }
    const activeSession = sessionRef.current;
    if (activeSession === null) return;

    setCards((current) => current.map((value) => value.id === card.id
      ? { ...value, busy: true, error: null }
      : value));
    try {
      await localRepository.removeImportSessionEntry(activeSession.sessionId, card.sessionEntryIndex);
      if (!mountedRef.current) return;
      revokePreviewUrl(card.previewUrl);
      setCards((current) => current.filter((value) => value.id !== card.id));
      await refreshSessionPlan();
    } catch (error) {
      if (!mountedRef.current) return;
      setCards((current) => current.map((value) => value.id === card.id
        ? {
            ...value,
            busy: false,
            error: error instanceof Error ? error.message : 'Could not remove the session entry.',
          }
        : value));
    }
  }

  async function startFreshSession(): Promise<void> {
    const view = await localRepository.getOrCreateOpenImportSession();
    if (!mountedRef.current) return;
    setSession(view.session);
    setProposals(view.proposals);
  }

  async function finishSession(): Promise<void> {
    const activeSession = sessionRef.current;
    if (activeSession === null || cards.some((card) => card.busy)) return;
    setSessionBusy(true);
    setMessage(null);
    try {
      const result = await localRepository.finishImportSession(activeSession.sessionId);
      if (result.createdClaimIds.length > 0) scheduleLocalMarketProcessing();
      if (!mountedRef.current) return;

      const rejected = result.proposals.filter((proposal) =>
        proposal.status === 'conflicting-continuation'
        || proposal.status === 'ambiguous-lineage'
        || proposal.status === 'unassigned-page').length;
      revokeAllCardPreviews();
      setCards([]);
      setSession(null);
      setProposals([]);
      setMessage({
        tone: rejected > 0 ? 'warn' : 'good',
        text: `${result.createdClaimIds.length} grouping claim${result.createdClaimIds.length === 1 ? '' : 's'} created. ${rejected > 0 ? `${rejected} proposed set${rejected === 1 ? '' : 's'} remained unassigned or conflicted; the captures are still in the library.` : 'The finished search membership is now immutable and rebuildable.'}`,
      });
      await startFreshSession();
    } catch (error) {
      if (!mountedRef.current) return;
      await refreshSessionPlan().catch(() => undefined);
      setMessage({
        tone: 'bad',
        text: error instanceof Error ? error.message : 'Could not finish the import session.',
      });
    } finally {
      if (mountedRef.current) setSessionBusy(false);
    }
  }

  async function discardSession(): Promise<void> {
    const activeSession = sessionRef.current;
    if (activeSession === null || cards.some((card) => card.busy)) return;
    if (!window.confirm('Discard this grouping session? Saved captures will remain in the local library.')) return;
    setSessionBusy(true);
    setMessage(null);
    try {
      await localRepository.discardImportSession(activeSession.sessionId);
      if (!mountedRef.current) return;
      revokeAllCardPreviews();
      setCards([]);
      setSession(null);
      setProposals([]);
      setMessage({
        tone: 'good',
        text: 'The grouping draft was discarded. Its saved captures remain available in the capture library.',
      });
      await startFreshSession();
    } catch (error) {
      if (!mountedRef.current) return;
      setMessage({
        tone: 'bad',
        text: error instanceof Error ? error.message : 'Could not discard the import session.',
      });
    } finally {
      if (mountedRef.current) setSessionBusy(false);
    }
  }

  async function exportDebugData(): Promise<void> {
    if (exportBusy) return;
    setExportBusy(true);
    setMessage(null);
    try {
      const exported = await localRepository.exportDebugData();
      downloadFredrickDebugExport(exported);
      if (!mountedRef.current) return;
      setMessage({
        tone: 'good',
        text: `Exported ${exported.counts.captures} capture${exported.counts.captures === 1 ? '' : 's'}, ${exported.counts.searchClaims} search claim${exported.counts.searchClaims === 1 ? '' : 's'}, and the current text-only repository state. ${exported.counts.evidenceImagesOmitted} private evidence image${exported.counts.evidenceImagesOmitted === 1 ? '' : 's'} were omitted.`,
      });
    } catch (error) {
      if (!mountedRef.current) return;
      setMessage({
        tone: 'bad',
        text: error instanceof Error ? error.message : 'Could not export local debug data.',
      });
    } finally {
      if (mountedRef.current) setExportBusy(false);
    }
  }

  const saved = cards.filter((card) => card.persistence === 'saved').length;
  const reused = cards.filter((card) => card.persistence === 'reused').length;
  const duplicateEntries = cards.filter((card) => card.persistence === 'duplicate').length;
  const persistedEntries = cards.filter((card) => card.sessionEntryIndex !== null).length;
  const working = cards.some((card) => card.busy);
  const canFinish = persistedEntries > 0 && !working && !sessionBusy;
  const canDiscard = cards.length > 0 && !working && !sessionBusy;

  return <div className="page-width content-page">
    <div className="page-heading import-heading">
      <div>
        <p className="eyebrow">Import session</p>
        <h1>Group Owl pages explicitly</h1>
        <p>Each screenshot is saved immediately, but it does not become part of a market search until you finish this persistent session. Duplicate images stay visible and can anchor a later page to one existing search lineage.</p>
      </div>
      <div className="heading-actions">
        <button className="button secondary" type="button" disabled={!canDiscard} onClick={() => void discardSession()}>Discard session</button>
        <button className="button primary" type="button" disabled={!canFinish} onClick={() => void finishSession()}>Finish session</button>
      </div>
    </div>

    <div className="notice good">Keep pages from one Owl search contiguous. Removing a card changes only this grouping draft; discarding a session never deletes captures. Reloading or navigating away preserves the open session.</div>

    <details className="panel developer-export-panel">
      <summary>Developer data export</summary>
      <div className="developer-export-content">
        <div>
          <strong>Download the complete local repository as readable JSON.</strong>
          <p>Includes captures, transcriptions, revisions, session entries and proposals, claims, sync state, and derived market records. The private evidence table and every image byte are excluded.</p>
        </div>
        <button
          className="button secondary"
          type="button"
          disabled={exportBusy}
          onClick={() => void exportDebugData()}
        >{exportBusy ? 'Preparing export…' : 'Download debug JSON'}</button>
      </div>
    </details>

    <section className="panel upload-panel">
      <button
        className={`drop-zone ${dragging ? 'dragging' : ''}`}
        type="button"
        disabled={sessionBusy}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          void addFiles([...event.dataTransfer.files]);
        }}
      >
        <span className="upload-icon">⇧</span>
        <strong>{sessionBusy ? 'Opening local session…' : 'Drop Owl screenshots here'}</strong>
        <span>or choose opaque PNG files · indexed PNG8, RGB, and RGBA are supported</span>
      </button>
      <input
        ref={inputRef}
        hidden
        type="file"
        accept="image/png,.png"
        multiple
        onChange={(event) => {
          if (event.target.files) void addFiles([...event.target.files]);
          event.target.value = '';
        }}
      />
    </section>

    {message && <div className={`notice ${message.tone}`}>{message.text}</div>}

    {cards.length > 0 && <div className="preview-summary">
      <span><strong>{cards.length}</strong> session entries</span>
      <span><strong>{saved}</strong> new captures</span>
      <span><strong>{reused}</strong> existing captures</span>
      <span><strong>{duplicateEntries}</strong> repeated entries</span>
    </div>}

    {proposals.length > 0 && <section className="panel session-plan-panel">
      <div className="section-heading">
        <div>
          <h2>Proposed search sets</h2>
          <p>These are calculated from the current preferred revisions and accepted claim heads. Only green proposals create immutable claims.</p>
        </div>
        <span>{proposals.length} set{proposals.length === 1 ? '' : 's'}</span>
      </div>
      <div className="session-proposal-list">
        {proposals.map((proposal) => <article className="session-proposal" key={`${proposal.proposalIndex}-${proposal.status}`}>
          <div className="session-proposal-main">
            <div className="state-stack">
              <StatusPill tone={proposalTone(proposal.status)}>{proposalLabel(proposal.status)}</StatusPill>
              {proposal.itemName && <StatusPill tone="neutral">{proposal.itemName}</StatusPill>}
              {proposal.expectedPageCount !== null && <StatusPill tone="neutral">Pages {proposal.presentPages.join(', ') || '—'} / {proposal.expectedPageCount}</StatusPill>}
            </div>
            <strong>{proposal.sourceImageIds.length} distinct source{proposal.sourceImageIds.length === 1 ? '' : 's'}</strong>
          </div>
          <p>{proposalExplanation(proposal)}</p>
          {(proposal.searchId || proposal.parentClaimId) && <div className="proposal-identities">
            {proposal.searchId && <span>Search <code>{proposal.searchId}</code></span>}
            {proposal.parentClaimId && <span>Parent <code>{proposal.parentClaimId}</code></span>}
          </div>}
        </article>)}
      </div>
    </section>}

    <div className="screenshot-list">
      {cards.map((card) => {
        const candidate = card.candidate;
        const transcription = candidate?.transcription ?? null;
        return <article className="screenshot-card" key={card.id}>
          <div className="screenshot-preview"><img src={card.previewUrl} alt={candidate ? 'Detected Owl result window' : card.fileName}/></div>
          <div className="screenshot-content">
            <div className="card-title-row">
              <div>
                <strong>{card.fileName}</strong>
                <small>{dateTime.format(card.observedAt)}{card.sessionEntryIndex === null ? '' : ` · session entry ${card.sessionEntryIndex + 1}`}</small>
              </div>
              <button
                className="icon-button"
                type="button"
                disabled={card.busy}
                onClick={() => void removeCard(card)}
                aria-label={card.busy ? 'Import is still processing' : 'Remove from grouping session'}
                title={card.busy ? 'Wait for this import to finish' : 'Remove from grouping session; keep saved capture'}
              >×</button>
            </div>

            {card.busy && <div className="working"><span className="spinner"/>Decoding, deduplicating, transcribing, and saving…</div>}
            {card.error && <div className="inline-error">{card.error}</div>}
            {card.persistence === 'reused' && <div className="notice warn compact-notice">This source already existed locally. Its retained revision and evidence were reused, and a new session entry was appended.</div>}
            {card.persistence === 'duplicate' && <div className="notice warn compact-notice">This source appears more than once in the open session. The repeated entry stays visible, but each proposed set uses the source only once.</div>}

            {candidate && transcription && <>
              <div className="decode-meta">
                <StatusPill tone={card.persistence === 'saved' ? 'good' : 'neutral'}>{card.persistence === 'saved' ? 'New local capture' : 'Existing local capture'}</StatusPill>
                <StatusPill tone={transcription.itemName === null ? 'warn' : 'good'}>{transcription.itemName ?? 'Item unreadable'}</StatusPill>
                <StatusPill tone="neutral">Page {transcription.page ?? '—'}/{transcription.totalPages ?? '—'}</StatusPill>
                <StatusPill tone="neutral">{transcription.totalResults ?? '—'} results</StatusPill>
                <StatusPill tone={candidate.partial ? 'warn' : 'good'}>{candidate.partial ? 'Partial candidate' : 'Complete candidate'}</StatusPill>
              </div>

              <dl className="identity-grid">
                <div><dt>Source image</dt><dd><code>{candidate.capture.sourceImage.sourceImageId}</code></dd></div>
                <div><dt>Private evidence</dt><dd><code>{candidate.evidence.evidenceImageId}</code></dd></div>
                <div><dt>Revision</dt><dd><code>{candidate.revisionId}</code></dd></div>
              </dl>

              <div className="read-table-wrap">
                <table className="read-table">
                  <thead><tr><th>Seller</th><th>Store</th><th>Units</th><th>Unit price</th><th>Owl qty</th></tr></thead>
                  <tbody>{transcription.listings.map((row) => <tr key={row.rowIndex}>
                    <td>{row.sellerName ?? '—'}</td>
                    <td>{row.storeName ?? '—'}</td>
                    <td>{row.bundle?.toLocaleString() ?? '—'}</td>
                    <td>{row.price === null ? '—' : formatMeso(row.price)}</td>
                    <td>{row.quantity?.toLocaleString() ?? '—'}</td>
                  </tr>)}</tbody>
                </table>
              </div>
            </>}
          </div>
        </article>;
      })}
    </div>
  </div>;
}
